import { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Volume2, Square, Loader2, ArrowRight, Save, Clock, Lock, BookOpen, Headphones, UserCircle, MessageSquare, Forward, Search, Send, Users, FileText, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AppLogo } from "@/components/AppLogo";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  "Negocios": "bg-blue-500/20 text-blue-400 border-blue-500/30",
  "Projetos": "bg-purple-500/20 text-purple-400 border-purple-500/30",
  "Conteudo": "bg-amber-500/20 text-amber-400 border-amber-500/30",
  "Pessoal": "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
};

function SharedIdeaContent() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, token: authToken, user } = useAuth();
  const [idea, setIdea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const synthRef = useRef(null);

  // Reply state
  const [showReply, setShowReply] = useState(false);
  const [replyMessage, setReplyMessage] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [replySent, setReplySent] = useState(false);

  // Forward state
  const [showForward, setShowForward] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [availableUsers, setAvailableUsers] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [forwardMessage, setForwardMessage] = useState("");
  const [selectedForwardUser, setSelectedForwardUser] = useState(null);
  const [sendingForward, setSendingForward] = useState(false);

  useEffect(() => {
    synthRef.current = window.speechSynthesis || null;
    return () => { synthRef.current?.cancel(); };
  }, []);

  useEffect(() => {
    const fetchIdea = async () => {
      try {
        const res = await axios.get(`${API}/shared/${token}`);
        if (res.data.expired) {
          setError("expired");
        } else {
          setIdea(res.data);
        }
      } catch (err) {
        if (err.response?.status === 410) {
          setError("revoked");
        } else {
          setError("not_found");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchIdea();
  }, [token]);

  const handlePlay = () => {
    if (!synthRef.current || !idea) return;
    if (isPlaying) {
      synthRef.current.cancel();
      setIsPlaying(false);
      return;
    }
    // Split into chunks for reliable playback on long texts
    const chunks = (idea.content.match(/[^.!?\n]+[.!?\n]*/g) || [idea.content]).reduce((acc, s) => {
      const last = acc[acc.length - 1];
      if (last && (last + s).length < 200) { acc[acc.length - 1] = last + s; }
      else { acc.push(s); }
      return acc;
    }, []);
    let idx = 0;
    const speakNext = () => {
      if (idx >= chunks.length) { setIsPlaying(false); return; }
      const utt = new SpeechSynthesisUtterance(chunks[idx].trim());
      utt.lang = "pt-BR";
      utt.onend = () => { idx++; speakNext(); };
      utt.onerror = (e) => { if (e.error !== "interrupted" && e.error !== "cancelled") setIsPlaying(false); };
      synthRef.current.speak(utt);
    };
    speakNext();
    setIsPlaying(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit", month: "long", year: "numeric",
    });
  };

  // Reply to sender
  const handleReply = async () => {
    if (!replyMessage.trim() || !idea?.shared_by) return;
    setSendingReply(true);
    try {
      await axios.post(`${API}/messages/reply-to-share`, {
        idea_title: idea.title,
        idea_content: idea.content,
        idea_category: idea.category || "Pessoal",
        recipient_id: idea.shared_by,
        message: replyMessage.trim(),
      }, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setReplySent(true);
      setShowReply(false);
      toast.success("Resposta enviada!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar resposta");
    } finally {
      setSendingReply(false);
    }
  };

  // Search users for forwarding
  useEffect(() => {
    if (!showForward || !searchQuery.trim() || !authToken) {
      if (!searchQuery.trim()) setAvailableUsers([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchingUsers(true);
      try {
        const res = await axios.get(`${API}/users/available?q=${encodeURIComponent(searchQuery)}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        setAvailableUsers(res.data.users || []);
      } catch {
        setAvailableUsers([]);
      } finally {
        setSearchingUsers(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, showForward, authToken]);

  // Forward idea
  const handleForward = async () => {
    if (!selectedForwardUser || !idea) return;
    setSendingForward(true);
    try {
      await axios.post(`${API}/messages/reply-to-share`, {
        idea_title: idea.title,
        idea_content: idea.content,
        idea_category: idea.category || "Pessoal",
        recipient_id: selectedForwardUser.id,
        message: forwardMessage.trim() || `Encaminhou a ideia "${idea.title}"`,
      }, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      toast.success(`Ideia encaminhada para ${selectedForwardUser.name}!`);
      setShowForward(false);
      setSelectedForwardUser(null);
      setForwardMessage("");
      setSearchQuery("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao encaminhar");
    } finally {
      setSendingForward(false);
    }
  };

  const getInitials = (name) => {
    return name?.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase() || "?";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--vi-page)' }}>
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (error) {
    const isExpired = error === "expired";
    const isRevoked = error === "revoked";
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ backgroundColor: 'var(--vi-page)' }}>
        <div className="text-center max-w-md">
          <div className={`w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center ${isExpired ? "bg-amber-500/10" : "bg-rose-500/10"}`}>
            {isExpired ? <Clock className="w-7 h-7 text-amber-400" /> : <Lock className="w-7 h-7 text-rose-400" />}
          </div>
          <h1 className="text-xl font-bold mb-2" style={{ color: 'var(--vi-text)' }}>
            {isExpired ? "Link expirado" : isRevoked ? "Link revogado" : "Link indisponível"}
          </h1>
          <p className="mb-6" style={{ color: 'var(--vi-text-muted)' }}>
            {isExpired
              ? "O prazo de validade deste link de compartilhamento expirou."
              : isRevoked
              ? "O autor revogou o acesso a este link."
              : "Ideia não encontrada ou link inválido."}
          </p>
          <Link to="/">
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2">
              Conhecer Audio Ideias <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const catKey = idea.category?.normalize("NFD").replace(/[\u0300-\u036f]/g, "") || "Pessoal";
  const canInteract = isAuthenticated && idea?.shared_by && idea.shared_by !== user?.id;

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--vi-page)' }} data-testid="shared-idea-page">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3 md:px-8" style={{ borderBottom: '1px solid var(--vi-border)' }}>
        <Link to="/">
          <AppLogo size="default" showText className="hidden sm:flex" />
          <AppLogo size="default" showText={false} className="sm:hidden" />
        </Link>
        <div className="flex items-center gap-3">
          {isAuthenticated && (
            <Link to="/mensagens">
              <Button variant="ghost" size="sm" className="text-xs gap-1.5" style={{ color: 'var(--vi-text-muted)' }}>
                <MessageSquare className="w-3.5 h-3.5" />
                Mensagens
              </Button>
            </Link>
          )}
          <span className="text-xs font-medium tracking-wider uppercase" style={{ color: 'var(--vi-text-muted)' }}>Ideia compartilhada</span>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 flex items-start justify-center px-4 py-8 md:py-12">
        <div className="w-full max-w-2xl space-y-5" data-testid="shared-idea-card">

          {/* Title Card */}
          <div className="rounded-2xl p-6 md:p-8" style={{ backgroundColor: 'var(--vi-card)', border: '1px solid var(--vi-border-accent)' }}>
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              {idea.category && (
                <span className={`text-xs px-3 py-1 rounded-full border ${CATEGORY_COLORS[catKey] || CATEGORY_COLORS["Pessoal"]}`}>
                  {idea.category}
                </span>
              )}
              <span className="text-xs" style={{ color: 'var(--vi-text-muted)' }}>
                {formatDate(idea.shared_at)}
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold leading-tight" style={{ color: 'var(--vi-text)' }} data-testid="shared-idea-title">
              {idea.title}
            </h1>
            {idea.sender_name && (
              <p className="text-sm mt-3 flex items-center gap-2" style={{ color: 'var(--vi-text-muted)' }} data-testid="shared-idea-sender">
                <UserCircle className="w-4 h-4" />
                Compartilhado por <span className="font-medium" style={{ color: 'var(--vi-text-soft)' }}>{idea.sender_name}</span>
              </p>
            )}
            <p className="text-sm mt-2" style={{ color: 'var(--vi-text-muted)' }}>
              Voce pode ler o texto abaixo ou ouvir em audio.
            </p>
          </div>

          {/* Read Section */}
          <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: 'var(--vi-card)', border: '1px solid var(--vi-border-accent)' }}>
            <div className="flex items-center gap-2.5 px-6 py-3" style={{ borderBottom: '1px solid var(--vi-border)', backgroundColor: 'var(--vi-input-bg)' }}>
              <BookOpen className="w-4 h-4 text-indigo-400" />
              <span className="text-sm font-medium" style={{ color: 'var(--vi-text-soft)' }}>Leia a ideia</span>
            </div>
            <div
              className="px-6 py-5 md:px-8 md:py-6 max-h-[50vh] overflow-y-auto"
              data-testid="shared-idea-content"
            >
              <p className="text-base md:text-lg leading-loose whitespace-pre-wrap" style={{ color: 'var(--vi-text-soft)' }}>
                {idea.content}
              </p>
            </div>
          </div>

          {/* Attachments Section */}
          {idea.attachments && idea.attachments.length > 0 && (() => {
            const images = idea.attachments.filter(a => a.content_type?.startsWith("image/"));
            const files = idea.attachments.filter(a => !a.content_type?.startsWith("image/"));
            return (
              <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: 'var(--vi-card)', border: '1px solid var(--vi-border-accent)' }} data-testid="shared-idea-attachments">
                <div className="flex items-center gap-2.5 px-6 py-3" style={{ borderBottom: '1px solid var(--vi-border)', backgroundColor: 'var(--vi-input-bg)' }}>
                  <FileText className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-medium" style={{ color: 'var(--vi-text-soft)' }}>
                    Arquivos anexados ({idea.attachments.length})
                  </span>
                </div>
                <div className="px-6 py-4 space-y-3">
                  {/* Image previews */}
                  {images.length > 0 && (
                    <div className={`grid gap-2 ${images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`} data-testid="attachment-image-grid">
                      {images.map((att, idx) => (
                        <a
                          key={att.id || idx}
                          href={`${API}/files/${att.id}/download`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group relative rounded-xl overflow-hidden block"
                          style={{ border: '1px solid var(--vi-border-accent)' }}
                          data-testid={`attachment-image-${idx}`}
                        >
                          <img
                            src={`${API}/files/${att.id}/download`}
                            alt={att.filename}
                            className="w-full object-cover rounded-xl transition-transform duration-300 group-hover:scale-105"
                            style={{ maxHeight: images.length === 1 ? '400px' : '220px' }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-xl flex items-end">
                            <div className="p-3 flex items-center gap-2 w-full">
                              <Download className="w-4 h-4 text-white shrink-0" />
                              <span className="text-white text-xs truncate">{att.filename}</span>
                            </div>
                          </div>
                        </a>
                      ))}
                    </div>
                  )}
                  {/* Other files */}
                  {files.map((att, idx) => {
                    const sizeStr = att.size > 1024 * 1024
                      ? `${(att.size / (1024 * 1024)).toFixed(1)} MB`
                      : `${(att.size / 1024).toFixed(0)} KB`;
                    return (
                      <a
                        key={att.id || idx}
                        href={`${API}/files/${att.id}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 rounded-xl transition-colors hover:bg-indigo-500/5"
                        style={{ border: '1px solid var(--vi-border-accent)' }}
                        data-testid={`attachment-download-${idx}`}
                      >
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: 'var(--vi-input-bg)' }}>
                          <FileText className="w-5 h-5 text-indigo-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate" style={{ color: 'var(--vi-text)' }}>{att.filename}</p>
                          <p className="text-xs" style={{ color: 'var(--vi-text-muted)' }}>{sizeStr}</p>
                        </div>
                        <Download className="w-4 h-4 shrink-0 text-indigo-400" />
                      </a>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Listen Section */}
          <div className="rounded-2xl p-5 md:p-6" style={{ backgroundColor: 'var(--vi-card)', border: '1px solid var(--vi-border-accent)' }}>
            <div className="flex items-center gap-2.5 mb-4">
              <Headphones className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-medium" style={{ color: 'var(--vi-text-soft)' }}>Ouça a ideia</span>
            </div>
            <p className="text-xs mb-4" style={{ color: 'var(--vi-text-muted)' }}>
              Não pode ler agora? Clique abaixo para ouvir o conteúdo em áudio.
            </p>
            <Button
              onClick={handlePlay}
              className={`w-full h-12 rounded-xl gap-3 text-base font-medium transition-all ${
                isPlaying
                  ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30"
                  : "bg-indigo-600 hover:bg-indigo-700 text-white"
              }`}
              data-testid="shared-idea-play-button"
            >
              {isPlaying ? (
                <><Square className="w-5 h-5" /> Parar audio</>
              ) : (
                <><Volume2 className="w-5 h-5" /> Ouvir esta ideia</>
              )}
            </Button>
          </div>

          {/* Interaction Section */}
          <div className="rounded-2xl p-5 md:p-6" style={{ backgroundColor: 'var(--vi-card)', border: '1px solid var(--vi-border-accent)' }} data-testid="shared-idea-interaction">
            <div className="flex items-center gap-2.5 mb-4">
              <MessageSquare className="w-4 h-4 text-cyan-400" />
              <span className="text-sm font-medium" style={{ color: 'var(--vi-text-soft)' }}>Interagir</span>
            </div>

            {!isAuthenticated ? (
              /* Not logged in - CTA */
              <div className="text-center py-3">
                <p className="text-sm mb-4" style={{ color: 'var(--vi-text-muted)' }}>
                  Entre na plataforma para responder ou encaminhar esta ideia
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link to="/login">
                    <Button className="w-full sm:w-auto bg-cyan-600 hover:bg-cyan-700 text-white gap-2 rounded-xl px-6" data-testid="shared-login-button">
                      <MessageSquare className="w-4 h-4" />
                      Entrar para responder
                    </Button>
                  </Link>
                  <Link to="/cadastro">
                    <Button variant="ghost" className="w-full sm:w-auto border rounded-xl px-6" style={{ color: 'var(--vi-text-muted)', borderColor: 'var(--vi-border-accent)' }} data-testid="shared-register-button">
                      Criar conta gratis
                    </Button>
                  </Link>
                </div>
              </div>
            ) : canInteract ? (
              /* Logged in - can interact */
              <div className="space-y-3">
                {/* Buttons */}
                {!showReply && !showForward && (
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={() => { setShowReply(true); setShowForward(false); }}
                      className="flex-1 h-11 rounded-xl gap-2 bg-cyan-600 hover:bg-cyan-700 text-white"
                      disabled={replySent}
                      data-testid="shared-reply-button"
                    >
                      <MessageSquare className="w-4 h-4" />
                      {replySent ? "Resposta enviada!" : `Responder a ${idea.sender_name || "remetente"}`}
                    </Button>
                    <Button
                      onClick={() => { setShowForward(true); setShowReply(false); }}
                      className="flex-1 h-11 rounded-xl gap-2" style={{ backgroundColor: 'var(--vi-input-bg)', border: '1px solid var(--vi-border-accent)', color: 'var(--vi-text-soft)' }}
                      data-testid="shared-forward-button"
                    >
                      <Forward className="w-4 h-4" />
                      Encaminhar para alguem
                    </Button>
                  </div>
                )}

                {/* Reply form */}
                {showReply && (
                  <div className="space-y-3">
                    <p className="text-xs" style={{ color: 'var(--vi-text-muted)' }}>
                      Respondendo a <span className="text-cyan-400 font-medium">{idea.sender_name || "remetente"}</span>
                    </p>
                    <textarea
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      placeholder="Escreva sua resposta..."
                      autoFocus
                      className="w-full h-24 rounded-xl px-4 py-3 text-sm resize-none focus:outline-none"
                      style={{ backgroundColor: 'var(--vi-input-bg)', border: '1px solid var(--vi-border-accent)', color: 'var(--vi-text)', '--tw-placeholder-color': 'var(--vi-placeholder)' }}
                      data-testid="shared-reply-input"
                    />
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        onClick={() => { setShowReply(false); setReplyMessage(""); }}
                        className="flex-1 rounded-xl" style={{ border: '1px solid var(--vi-border-accent)', color: 'var(--vi-text-muted)' }}
                      >
                        Cancelar
                      </Button>
                      <Button
                        onClick={handleReply}
                        disabled={!replyMessage.trim() || sendingReply}
                        className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white gap-2 rounded-xl"
                        data-testid="shared-reply-send"
                      >
                        {sendingReply ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Enviar
                      </Button>
                    </div>
                  </div>
                )}

                {/* Forward form */}
                {showForward && (
                  <div className="space-y-3">
                    {!selectedForwardUser ? (
                      <>
                        <p className="text-xs" style={{ color: 'var(--vi-text-muted)' }}>Buscar usuário para encaminhar</p>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--vi-text-muted)' }} />
                          <Input
                            placeholder="Digite o nome..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10"
                            style={{ backgroundColor: 'var(--vi-input-bg)', borderColor: 'var(--vi-border-accent)', color: 'var(--vi-text)' }}
                            autoFocus
                            data-testid="shared-forward-search"
                          />
                        </div>
                        {searchingUsers && <Loader2 className="w-4 h-4 animate-spin text-cyan-500 mx-auto" />}
                        {availableUsers.length > 0 && (
                          <div className="space-y-1 max-h-40 overflow-y-auto">
                            {availableUsers.map(u => (
                              <button
                                key={u.id}
                                onClick={() => setSelectedForwardUser(u)}
                                className="w-full text-left px-3 py-2.5 rounded-xl flex items-center gap-3 hover:bg-cyan-500/10 transition-colors"
                                style={{ color: 'var(--vi-text-soft)' }}
                                data-testid={`forward-user-${u.id}`}
                              >
                                <div className="w-8 h-8 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center text-xs font-semibold">
                                  {getInitials(u.name)}
                                </div>
                                <span className="text-sm">{u.name}</span>
                              </button>
                            ))}
                          </div>
                        )}
                        {searchQuery.trim() && !searchingUsers && availableUsers.length === 0 && (
                          <p className="text-xs text-center py-2" style={{ color: 'var(--vi-text-muted)' }}>
                            Nenhum usuário encontrado. O usuário precisa ter habilitado o recebimento.
                          </p>
                        )}
                        <Button
                          variant="ghost"
                          onClick={() => { setShowForward(false); setSearchQuery(""); setAvailableUsers([]); }}
                          className="w-full rounded-xl" style={{ border: '1px solid var(--vi-border-accent)', color: 'var(--vi-text-muted)' }}
                        >
                          Cancelar
                        </Button>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: 'var(--vi-input-bg)', border: '1px solid var(--vi-border-accent)' }}>
                          <div className="w-9 h-9 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center text-sm font-semibold">
                            {getInitials(selectedForwardUser.name)}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium" style={{ color: 'var(--vi-text)' }}>{selectedForwardUser.name}</p>
                            <p className="text-[10px]" style={{ color: 'var(--vi-text-muted)' }}>Recebera esta ideia</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedForwardUser(null)}
                            className="text-xs" style={{ color: 'var(--vi-text-muted)' }}
                          >
                            Trocar
                          </Button>
                        </div>
                        <textarea
                          value={forwardMessage}
                          onChange={(e) => setForwardMessage(e.target.value)}
                          placeholder="Mensagem (opcional)..."
                          className="w-full h-20 rounded-xl px-4 py-3 text-sm resize-none focus:outline-none"
                          style={{ backgroundColor: 'var(--vi-input-bg)', border: '1px solid var(--vi-border-accent)', color: 'var(--vi-text)' }}
                          data-testid="shared-forward-message"
                        />
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            onClick={() => { setShowForward(false); setSelectedForwardUser(null); setForwardMessage(""); setSearchQuery(""); }}
                            className="flex-1 rounded-xl" style={{ border: '1px solid var(--vi-border-accent)', color: 'var(--vi-text-muted)' }}
                          >
                            Cancelar
                          </Button>
                          <Button
                            onClick={handleForward}
                            disabled={sendingForward}
                            className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white gap-2 rounded-xl"
                            data-testid="shared-forward-send"
                          >
                            {sendingForward ? <Loader2 className="w-4 h-4 animate-spin" /> : <Forward className="w-4 h-4" />}
                            Encaminhar
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ) : isAuthenticated ? (
              /* Logged in but is the sender */
              <p className="text-sm text-center py-2" style={{ color: 'var(--vi-text-muted)' }}>
                Esta e a sua propria ideia compartilhada.
              </p>
            ) : null}
          </div>

          {/* Save + Expiration */}
          {(idea.allow_save !== false || idea.expires_at) && (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              {idea.allow_save !== false && (
                <Button
                  onClick={() => {
                    const encoded = encodeURIComponent(idea.content);
                    navigate(`/?texto=${encoded}`);
                  }}
                  className="h-11 rounded-xl gap-2 px-5 flex-1"
                  style={{ backgroundColor: 'var(--vi-input-bg)', border: '1px solid var(--vi-border-accent)', color: 'var(--vi-text-soft)' }}
                  data-testid="shared-idea-save-button"
                >
                  <Save className="w-4 h-4" />
                  Salvar como minha ideia
                </Button>
              )}
              {idea.expires_at && (
                <div className="flex items-center justify-center gap-2 text-xs px-4 h-11 rounded-xl" style={{ border: '1px solid var(--vi-border)', color: 'var(--vi-text-muted)' }}>
                  <Clock className="w-3.5 h-3.5" />
                  <span>
                    Valido ate {new Date(idea.expires_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* CTA Banner */}
          <div className="rounded-2xl p-5 md:p-6 border border-indigo-500/20 bg-indigo-500/[0.05]" data-testid="shared-idea-cta">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="flex-1 text-center sm:text-left">
                <p className="font-medium mb-1" style={{ color: 'var(--vi-text)' }}>
                  Capture suas ideias por voz ou texto
                </p>
                <p className="text-sm" style={{ color: 'var(--vi-text-muted)' }}>
                  Grave, organize e ouça suas ideias com Audio Ideias
                </p>
              </div>
              <Link to="/">
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2 shrink-0 rounded-xl px-6">
                  Experimentar gratis <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="px-4 py-3" style={{ borderTop: '1px solid var(--vi-border)' }}>
        <p className="text-center text-xs" style={{ color: 'var(--vi-text-muted)' }}>
          Audio Ideias - Capture suas ideias por voz ou texto
        </p>
      </footer>
    </div>
  );
}

export default function SharedIdeaPage() {
  return (
    <ThemeProvider>
      <SharedIdeaContent />
    </ThemeProvider>
  );
}
